import { FormEvent, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Input, showToast } from "@xpense/ui";
import type { AssistantResponse } from "@xpense/types";
import { api } from "../api/client";
import { Icon } from "./Icons";

interface AssistantDockProps {
  onLogout: () => void;
}

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
}

export default function AssistantDock({ onLogout }: AssistantDockProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [pending, setPending] = useState<AssistantResponse["confirmation"] | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const context = () => ({
    platform: "web" as const,
    current_route: location.pathname,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const handleTextSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const prompt = text.trim();
    if (!prompt || busy) return;

    setText("");
    setMessages((prev) => [...prev, { role: "user", text: prompt }]);
    await sendText(prompt);
  };

  const sendText = async (prompt: string) => {
    setBusy(true);
    try {
      const response = await api.post<AssistantResponse>("/api/v1/assistant/text", {
        text: prompt,
        context: context(),
      });
      handleResponse(response, true);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Assistant request failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const sendAudio = async (blob: Blob) => {
    setBusy(true);
    const form = new FormData();
    form.append("audio", blob, "assistant-prompt.webm");
    form.append("context", JSON.stringify(context()));

    try {
      const response = await api.postForm<AssistantResponse>("/api/v1/assistant/audio", form);
      handleResponse(response, false);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Audio assistant request failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      showToast("Audio recording is not available in this browser", "error");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        recorderRef.current = null;
        setRecording(false);
        if (blob.size > 0) {
          void sendAudio(blob);
        }
      };

      recorder.start();
      setRecording(true);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Microphone access failed", "error");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const handleResponse = (response: AssistantResponse, userAlreadyAdded: boolean) => {
    if (!userAlreadyAdded && response.transcript) {
      setMessages((prev) => [...prev, { role: "user", text: response.transcript! }]);
    }

    setMessages((prev) => [...prev, { role: "assistant", text: response.reply }]);
    setPending(response.confirmation ?? null);
    speak(response.reply);
    void applyClientActions(response);
  };

  const confirmPending = async (decision: "confirm" | "cancel") => {
    if (!pending || busy) return;

    setBusy(true);
    try {
      const response = await api.post<AssistantResponse>("/api/v1/assistant/confirm", {
        action_id: pending.action_id,
        decision,
      });
      setPending(null);
      setMessages((prev) => [...prev, { role: "user", text: decision === "confirm" ? "Confirm" : "Cancel" }]);
      handleResponse(response, true);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Confirmation failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const applyClientActions = async (response: AssistantResponse) => {
    for (const action of response.client_actions ?? []) {
      if (action.type === "navigate") {
        navigate(action.route);
      } else if (action.type === "refresh") {
        window.dispatchEvent(new CustomEvent("xpense:assistant-refresh", { detail: action.resources }));
      } else if (action.type === "download_export") {
        downloadJson(action.filename, action.data);
      } else if (action.type === "start_gmail_oauth") {
        await startGmailOAuth();
      } else if (action.type === "logout") {
        onLogout();
      }
    }
  };

  const startGmailOAuth = async () => {
    try {
      const redirectUri = `${window.location.origin}/gmail-callback`;
      const { url } = await api.get<{ url: string; state: string }>(
        `/api/v1/gmail/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`
      );
      window.location.href = url;
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to start Gmail linking", "error");
    }
  };

  return (
    <div className={`xpn-assistant ${open ? "xpn-assistant--open" : ""}`}>
      {open && (
        <section className="xpn-assistant-panel" aria-label="Xpense assistant">
          <div className="xpn-assistant-header">
            <div className="xpn-assistant-title">
              <Icon name="message" />
              <span>Assistant</span>
            </div>
            <Button aria-label="Close assistant" iconOnly size="sm" variant="secondary" onClick={() => setOpen(false)}>
              <Icon name="chevronDown" />
            </Button>
          </div>

          <div className="xpn-assistant-messages">
            {messages.length === 0 ? (
              <div className="xpn-assistant-empty">Ready.</div>
            ) : (
              messages.map((message, index) => (
                <div
                  className={`xpn-assistant-bubble xpn-assistant-bubble--${message.role}`}
                  key={`${message.role}-${index}`}
                >
                  {message.text}
                </div>
              ))
            )}
          </div>

          {pending && (
            <div className="xpn-assistant-confirm">
              <div className="xpn-text-caption xpn-text-muted">{pending.preview}</div>
              <div className="xpn-assistant-confirm-actions">
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => confirmPending("cancel")}>
                  Cancel
                </Button>
                <Button size="sm" disabled={busy} onClick={() => confirmPending("confirm")}>
                  <Icon name="check" />
                  Confirm
                </Button>
              </div>
            </div>
          )}

          <form className="xpn-assistant-input" onSubmit={handleTextSubmit}>
            <Button
              aria-label={recording ? "Stop recording" : "Start recording"}
              iconOnly
              variant={recording ? "danger" : "secondary"}
              type="button"
              disabled={busy && !recording}
              onClick={recording ? stopRecording : startRecording}
            >
              <Icon name={recording ? "stop" : "mic"} />
            </Button>
            <Input
              aria-label="Assistant prompt"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Ask Xpense AI"
              disabled={busy}
            />
            <Button aria-label="Send prompt" iconOnly type="submit" disabled={busy || !text.trim()}>
              <Icon name="send" />
            </Button>
          </form>
        </section>
      )}

      <Button
        aria-label="Open assistant"
        className="xpn-assistant-trigger"
        iconOnly
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name={open ? "chevronDown" : "mic"} />
      </Button>
    </div>
  );
}

function speak(text: string) {
  if (!("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
}

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
